import { IndianRupee, Clock, Zap, MapPin, Weight, BoxIcon, Tag, Truck, Percent, Gift, Wallet } from "lucide-react";

const ZONE_COLORS = {
  A: "bg-emerald-50 text-emerald-700 border-emerald-200",
  B: "bg-blue-50 text-blue-700 border-blue-200",
  C: "bg-amber-50 text-amber-700 border-amber-200",
  D: "bg-red-50 text-red-700 border-red-200",
};

function EtaBadge({ days, size = "md" }) {
  if (!days) return null;
  const label = days === 1 ? "1 day" : `${days} days`;
  const isSmall = size === "sm";
  return (
    <span
      className={`inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold ${
        isSmall ? "px-2 py-0.5 rounded-md text-[10px]" : "px-2.5 py-1 rounded-lg text-xs"
      }`}
      data-testid="eta-badge"
    >
      <Clock className={isSmall ? "h-2.5 w-2.5" : "h-3 w-3"} />
      ~{label}
    </span>
  );
}

function ZoneBadge({ zone, zoneLabel }) {
  if (!zone) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-bold ${ZONE_COLORS[zone] || ZONE_COLORS.A}`}
      data-testid="zone-badge"
    >
      <MapPin className="h-3 w-3" />
      Zone {zone}
      {zoneLabel && (
        <span className="font-semibold opacity-80">· {zoneLabel}</span>
      )}
    </span>
  );
}

export { EtaBadge, ZoneBadge };

export default function PriceCard({ price, compact = false }) {
  if (!price) return null;

  const hasDiscounts = price.discounts_applied?.length > 0;
  const totalDiscount = price.total_discount || 0;

  if (compact) {
    return (
      <div className="inline-flex flex-col gap-0.5" data-testid="price-inline">
        <span className="text-sm font-bold text-slate-900 inline-flex items-center gap-0.5">
          <IndianRupee className="h-3.5 w-3.5" />{price.final_price ?? price.total}
          {totalDiscount > 0 && (
            <span className="text-[10px] font-medium text-slate-400 line-through ml-1">
              {price.base_total}
            </span>
          )}
        </span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {price.zone && <ZoneBadge zone={price.zone} zoneLabel={price.zone_label} />}
          {(price.estimated_delivery_days || price.estimated_days) && <EtaBadge days={price.estimated_delivery_days || price.estimated_days} size="sm" />}
          {totalDiscount > 0 && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-green-50 text-green-700 border border-green-200">
              <Tag className="h-2.5 w-2.5" />
              -{totalDiscount}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-orange-50 via-amber-50/50 to-white border border-orange-200/80 rounded-xl p-4 price-glow" data-testid="price-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FF6B00] rounded-lg flex items-center justify-center shadow-sm shadow-[#FF6B00]/20">
            <IndianRupee className="h-4 w-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-slate-800">Price Breakdown</span>
        </div>
        {(price.estimated_delivery_days || price.estimated_days) && <EtaBadge days={price.estimated_delivery_days || price.estimated_days} />}
      </div>

      {/* Zone & Distance Info */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {price.zone && <ZoneBadge zone={price.zone} zoneLabel={price.zone_label} />}
        {price.distance_km > 0 && (
          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg text-xs font-medium" data-testid="distance-badge">
            {price.distance_source === "osrm" && (
              <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1 py-0.5 rounded mr-0.5">ROAD</span>
            )}
            {price.distance_km} km
          </span>
        )}
        {price.delivery_speed === "lazy" && (
          <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-1 rounded-lg text-xs font-bold" data-testid="lazy-badge">
            <Clock className="h-3 w-3" />
            Lazy
          </span>
        )}
        {price.wallet_tier && (
          <span className="inline-flex items-center gap-1 bg-yellow-50 text-yellow-700 border border-yellow-300 px-2.5 py-1 rounded-lg text-xs font-bold" data-testid="b2b-badge">
            <Wallet className="h-3 w-3" />
            B2B {price.wallet_tier}
          </span>
        )}
        {price.surge?.applied && (
          <span className="inline-flex items-center gap-1 bg-[#FF6B00]/10 text-[#FF6B00] border border-[#FF6B00]/30 px-2.5 py-1 rounded-lg text-xs font-bold" data-testid="surge-badge">
            <Zap className="h-3 w-3" />
            Surge ×{price.surge.multiplier?.toFixed ? price.surge.multiplier.toFixed(2) : price.surge.multiplier}
          </span>
        )}
      </div>

      {/* ETA */}
      {price.estimated_days && (
        <div className="flex items-center gap-1.5 mb-3 px-3 py-2 bg-emerald-50/60 border border-emerald-100 rounded-lg" data-testid="eta-detail">
          <Zap className="h-3.5 w-3.5 text-emerald-600" />
          <span className="text-xs text-emerald-700 font-medium">
            Estimated delivery in <strong>{price.estimated_days === 1 ? "1 day" : `${price.estimated_days} days`}</strong>
            {price.priority_pickup && <span className="ml-1.5 text-[10px] font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded">PRIORITY 15 min</span>}
          </span>
        </div>
      )}

      {/* Bundle Info */}
      {price.bundle_label && (
        <div className="flex items-center gap-1.5 mb-3 px-3 py-2 bg-blue-50/60 border border-blue-100 rounded-lg" data-testid="bundle-detail">
          <Truck className="h-3.5 w-3.5 text-blue-600" />
          <span className="text-xs text-blue-700 font-medium">
            {price.bundle_label} — up to {price.bundle_max_items} items
          </span>
        </div>
      )}

      {/* Price Breakdown Lines */}
      <div className="space-y-2 text-xs">
        {(() => {
          // Sprint 56.1 — unified per-parcel breakdown for BOTH single
          // and multi-parcel orders. Single-parcel synthesizes one
          // pseudo-row from the existing slab fields so the customer
          // sees the same "Parcel #1 → Weight / Weight slab / Service"
          // shape no matter how many parcels they booked.
          if (price.bundle_label) return null;
          let blocks = null;
          if (price.multi_item && Array.isArray(price.items_breakdown) && price.items_breakdown.length > 0) {
            blocks = price.items_breakdown;
          } else {
            // Synthesize a Parcel #1 from the legacy slab quote.
            const slabInclusive =
              (price.weight_charge_display ?? price.weight_charge ?? price.slab_charge ?? 0)
              + (price.longhaul_surcharge ?? 0);
            const preGst = price.service_charges ?? 0;
            const serviceOps = Math.max(0, preGst - slabInclusive);
            blocks = [{
              item_id: "single",
              item_name: null,
              chargeable_weight_kg: price.chargeable_weight_kg,
              actual_weight_kg: price.actual_weight_kg,
              weight_slab: price.weight_slab,
              quantity: 1,
              line_slab_charge: slabInclusive,
              line_service_charges: serviceOps,
              line_pre_gst: preGst,
            }];
          }
          return (
            <div className="space-y-2.5" data-testid="multi-parcel-breakdown">
              {blocks.map((it, idx) => (
                <div
                  key={it.item_id || `p-${idx}`}
                  className="rounded-lg border border-orange-100 bg-white/70 px-3 py-2.5"
                  data-testid={`parcel-block-${idx}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#FF6B00] flex items-center gap-1">
                      <BoxIcon className="h-3 w-3" />
                      Parcel #{idx + 1}
                      {it.item_name && <span className="text-slate-600 font-semibold normal-case tracking-normal">· {it.item_name}</span>}
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {it.weight_slab || `${it.chargeable_weight_kg ?? it.actual_weight_kg ?? "?"} kg`}
                      {(it.quantity ?? 1) > 1 && ` · qty ${it.quantity}`}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {/* Weight row — explicit so the customer always sees what they're being billed on */}
                    {(it.chargeable_weight_kg ?? it.actual_weight_kg) && (
                      <div className="flex justify-between text-slate-500" data-testid={`parcel-${idx}-weight`}>
                        <span className="flex items-center gap-1">
                          <Weight className="h-3 w-3 text-slate-400" />
                          Weight
                        </span>
                        <span className="font-medium text-slate-700">
                          {it.chargeable_weight_kg ?? it.actual_weight_kg} kg
                          {it.volumetric_weight_kg > (it.actual_weight_kg ?? 0) && (
                            <span className="text-[9px] text-slate-400 ml-1">(volumetric)</span>
                          )}
                        </span>
                      </div>
                    )}
                    {(it.line_slab_charge ?? 0) > 0 && (
                      <div className="flex justify-between text-slate-500" data-testid={`parcel-${idx}-slab`}>
                        <span className="flex items-center gap-1">
                          <Tag className="h-3 w-3 text-slate-400" />
                          Weight slab charge
                        </span>
                        <span className="font-medium text-slate-700">{price.currency} {it.line_slab_charge}</span>
                      </div>
                    )}
                    {(it.line_service_charges ?? 0) > 0 && (
                      <div className="flex justify-between text-slate-500" data-testid={`parcel-${idx}-service`}>
                        <span className="flex items-center gap-1">
                          <Truck className="h-3 w-3 text-slate-400" />
                          Service charges
                        </span>
                        <span className="font-medium text-slate-700">{price.currency} {it.line_service_charges}</span>
                      </div>
                    )}
                    {/* Show subtotal only when there are 2+ parcels, otherwise it's just noise. */}
                    {blocks.length > 1 && (
                      <div className="flex justify-between pt-1 mt-1 border-t border-dashed border-orange-100" data-testid={`parcel-${idx}-subtotal`}>
                        <span className="font-semibold text-slate-700">Parcel #{idx + 1} subtotal</span>
                        <span className="font-bold text-slate-900">{price.currency} {it.line_pre_gst ?? it.line_total}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* ── Common footer (shared by single + multi-parcel) ────────── */}
        {!price.bundle_label && (
          <>
            {/* GST — shown once at the bottom regardless of parcel count */}
            {price.gst > 0 && (
              <div className="flex justify-between text-slate-500" data-testid="gst-line">
                <span className="flex items-center gap-1">
                  <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1 py-0.5 rounded">GST</span>
                  Tax ({price.gst_rate_pct ?? 18}%)
                </span>
                <span className="font-medium text-slate-700">{price.currency} {price.gst}</span>
              </div>
            )}
            {(price.cod_fee ?? 0) > 0 && (
              <div className="flex justify-between text-amber-700" data-testid="cod-fee-line">
                <span className="flex items-center gap-1.5">
                  <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1 py-0.5 rounded">COD</span>
                  Cash-on-Delivery Fee ({price.cod_fee_pct ?? 5}%)
                </span>
                <span className="font-medium text-amber-700">{price.currency} {price.cod_fee}</span>
              </div>
            )}
          </>
        )}

        {/* Bundle price line */}
        {price.bundle_label && (
          <>
            <div className="flex justify-between text-slate-500">
              <span className="flex items-center gap-1">
                <Truck className="h-3 w-3 text-blue-400" />
                {price.bundle_label} (Zone {price.zone})
              </span>
              <span className="font-medium text-slate-700">{price.currency} {price.bundle_base_price}</span>
            </div>
            {price.bundle_gst > 0 && (
              <div className="flex justify-between text-slate-500">
                <span className="flex items-center gap-1">
                  <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1 py-0.5 rounded">GST</span>
                  Tax (18%)
                </span>
                <span className="font-medium text-slate-700">{price.currency} {price.bundle_gst}</span>
              </div>
            )}
          </>
        )}

        {/* Subtotal before discounts */}
        {hasDiscounts && (
          <div className="flex justify-between text-slate-500 border-t border-slate-100 pt-2">
            <span className="font-medium">Subtotal</span>
            <span className="font-medium text-slate-700">{price.currency} {price.base_total}</span>
          </div>
        )}

        {/* Discounts */}
        {hasDiscounts && (
          <div className="space-y-1.5 bg-green-50/60 border border-green-100 rounded-lg px-3 py-2" data-testid="discounts-section">
            <p className="text-[10px] font-bold text-green-700 uppercase tracking-wider flex items-center gap-1">
              <Tag className="h-3 w-3" /> Discounts Applied
            </p>
            {price.discounts_applied.map((d, i) => {
              const isSurge = d.type === "surge";
              return (
              <div key={d.type || i} className={`flex justify-between ${isSurge ? "text-[#FF6B00]" : "text-green-700"}`} data-testid={`discount-${d.type}`}>
                <span className="flex items-center gap-1 text-xs">
                  {d.type === "lazy_delivery" && <Clock className="h-3 w-3" />}
                  {d.type === "route_density" && <Truck className="h-3 w-3" />}
                  {d.type === "b2b_tier" && <Wallet className="h-3 w-3" />}
                  {d.type === "b2b_flat_rate" && <Zap className="h-3 w-3" />}
                  {d.type === "coupon" && <Gift className="h-3 w-3" />}
                  {d.type === "bundle" && <Percent className="h-3 w-3" />}
                  {isSurge && <Zap className="h-3 w-3" />}
                  {d.label}
                </span>
                <span className="font-bold text-xs">{isSurge ? "+" : "-"}{price.currency} {isSurge ? d.surcharge : (d.discount || d.savings || 0)}</span>
              </div>
            );})}
          </div>
        )}

        {/* Volume reward notice */}
        {price.volume_reward_eligible && (
          <div className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="volume-reward-notice">
            <Gift className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-xs text-amber-700 font-medium">{price.volume_reward_note}</span>
          </div>
        )}

        {/* Final Total */}
        <div className="border-t border-orange-200/60 pt-2.5 mt-2 flex justify-between items-center">
          <span className="font-bold text-slate-900 text-sm tracking-tight">
            {hasDiscounts ? "YOU PAY" : "TOTAL"}
          </span>
          <div className="text-right">
            {totalDiscount > 0 && (
              <span className="text-xs text-slate-400 line-through mr-2">{price.currency} {price.base_total}</span>
            )}
            <span className="font-extrabold text-[#FF6B00] text-lg tracking-tight" data-testid="final-price">
              {price.currency} {price.final_price ?? price.total}
            </span>
            {totalDiscount > 0 && (
              <span className="block text-[10px] font-bold text-green-600 mt-0.5">
                You save INR {totalDiscount}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
